import React, { useEffect } from 'react';
import { ArrowLeft, Layers, AlertTriangle } from 'lucide-react';
import { platformName, supportEmail } from '../../lib/site';
import { LEGAL } from '../../content/legal';

const heading = { fontFamily: "'Outfit', sans-serif" };

export const LegalPage = ({ title, intro, sections, testId }) => {
  const name = platformName();
  const contact = supportEmail() || LEGAL.contact;
  useEffect(() => {
    document.title = `${title} — ${name}`; window.scrollTo(0, 0);
    if (!document.getElementById('platform-fonts')) {
      const l = document.createElement('link'); l.id = 'platform-fonts'; l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap'; document.head.appendChild(l);
    }
  }, [title, name]);
  return (
    <div data-testid={testId} className="min-h-screen bg-slate-950 text-slate-50" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <header className="sticky top-0 z-40 border-b border-slate-800/80 bg-slate-950/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          <a href="/" data-testid="legal-home-link" className="flex items-center gap-2.5" style={heading}>
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-indigo-600"><Layers size={16} /></span>
            <span className="text-lg font-extrabold tracking-tight">{name}</span>
          </a>
          <a href="/" data-testid="legal-back-link" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"><ArrowLeft size={15} /> Back to site</a>
        </div>
      </header>
      <main className="mx-auto grid max-w-5xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[220px_1fr] lg:py-16">
        <aside className="hidden lg:block">
          <nav className="sticky top-24 space-y-1 text-sm">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-indigo-400">Contents</p>
            {sections.map((s) => <a key={s.id} href={`#${s.id}`} className="block truncate py-1 text-slate-400 transition-colors hover:text-slate-100">{s.h}</a>)}
            <div className="mt-6 border-t border-slate-800 pt-4 text-xs text-slate-500">
              <a href="/privacy" data-testid="legal-nav-privacy" className="block py-1 hover:text-slate-200">Privacy Policy</a>
              <a href="/terms" data-testid="legal-nav-terms" className="block py-1 hover:text-slate-200">Terms of Service</a>
            </div>
          </nav>
        </aside>
        <article className="min-w-0">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-indigo-400">Legal</p>
          <h1 data-testid="legal-title" className="mt-3 text-4xl font-extrabold tracking-tight sm:text-5xl" style={heading}>{title}</h1>
          <p data-testid="legal-effective" className="mt-3 text-sm text-slate-500">Effective {LEGAL.effective}</p>
          {LEGAL.draft && (
            <div data-testid="legal-draft-banner" className="mt-6 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-950/30 p-4 text-sm text-amber-200">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span><b>Draft — pending legal review.</b> Prepared for {LEGAL.entity}, governed by the laws of Florida. This template is not legal advice; we recommend review by counsel before it is published as final.</span>
            </div>
          )}
          <p className="mt-6 text-base leading-relaxed text-slate-300 sm:text-lg">{intro}</p>
          <div className="mt-10 space-y-10">
            {sections.map((s) => (
              <section key={s.id} id={s.id} data-testid={`legal-section-${s.id}`} className="scroll-mt-24">
                <h2 className="text-xl font-bold text-slate-50 sm:text-2xl" style={heading}>{s.h}</h2>
                {s.p.map((para, i) => <p key={i} className="mt-3 leading-relaxed text-slate-400">{para}</p>)}
              </section>
            ))}
          </div>
          <footer className="mt-16 border-t border-slate-800 pt-6 text-sm text-slate-500">
            Questions about this document? Email <a href={`mailto:${contact}`} data-testid="legal-contact-link" className="text-indigo-300">{contact}</a>.
            <span className="mx-2">·</span><a href="/privacy" className="hover:text-slate-200">Privacy Policy</a>
            <span className="mx-2">·</span><a href="/terms" className="hover:text-slate-200">Terms of Service</a>
          </footer>
        </article>
      </main>
    </div>
  );
};
